import React, { useCallback, useState } from "react";
import { Button, ErrorMessage, SelectableCards, Text } from "@jsq/ds/core";
import { PaymentMethod } from "../../../../common/enums";
import { WorkflowMode } from "../../common/enums";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { type ChildComponentProps } from "../types";
import * as styles from "./styles_vanilla.css";
import { type PaymentOption } from "./types";

export const SelectPaymentMethod: React.FC<ChildComponentProps> = ({
  onBackButtonName = "Back",
  onContinueClick,
  onBackClick,
  onBeforeContinue,
}) => {
  const {
    paymentMethods,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    workflowMode,
    otherTileConfig,
  } = useConsolidatedPaymentPrefContext();

  const [selected, setSelected] = useState<string | number | undefined>(selectedPaymentMethod);
  const [showError, setShowError] = useState(false);

  const handleFormSubmission = useCallback(() => {
    if (!selected) {
      setShowError(true);
      return;
    }

    setSelectedPaymentMethod(selected as PaymentMethod);
    onBeforeContinue?.(selected as PaymentMethod);

    // In Create mode, advance the step because the form is added AFTER this component.
    // In Edit mode, don't advance the step because selecting a payment method causes
    // this component to be removed and replaced by the form at the SAME index.
    if (workflowMode === WorkflowMode.Create) {
      onContinueClick();
    }
  }, [onBeforeContinue, onContinueClick, selected, setSelectedPaymentMethod, workflowMode]);

  const paymentOptions: PaymentOption[] = [
    {
      title: "ACH direct deposit",
      details: "Requires a US bank account",
      icon: "university",
      value: PaymentMethod.ACH,
    },
    {
      title: "Wire transfer",
      details: "Supports SWIFT wires",
      icon: "money-bill-transfer",
      value: PaymentMethod.Wire,
    },
    {
      title: "Check",
      details: "Requires an address",
      icon: "money-check",
      value: PaymentMethod.Check,
    },
    {
      title: otherTileConfig?.label ?? "Other",
      details: "Custom payment instructions",
      icon: "pen-to-square",
      value: PaymentMethod.Other,
    },
  ];

  // Adjust options based on availablePaymentMethods
  const adjustedOptions = paymentOptions.map((option) => {
    const paymentMethodValue = paymentMethods?.[option.value];
    return {
      ...option,
      disabled: !paymentMethodValue?.isAvailable,
    };
  });

  const hasAvailablePaymentMethods = adjustedOptions.find((option) => !option.disabled);

  return (
    <>
      <Text className={styles.title} variant="h3">
        {workflowMode === WorkflowMode.Edit
          ? "Update payment instructions"
          : "Create new payment instructions"}
      </Text>
      <Text variant="h4">Select payment method</Text>
      <SelectableCards
        value={selected}
        onSelectOption={setSelected}
        className={styles.options}
        id="paymentPrefSelectPaymentMethod"
        options={adjustedOptions}
      />
      {!hasAvailablePaymentMethods && (
        <ErrorMessage>No supported payment methods available.</ErrorMessage>
      )}
      {showError && !selected && (
        <ErrorMessage>You must select a payment method to continue</ErrorMessage>
      )}
      <div className={styles.footer}>
        <Button
          className={styles.backButton}
          variant="neutral"
          id="paymentPrefSelectPaymentsBackButton"
          onClick={onBackClick}
        >
          {onBackButtonName}
        </Button>
        <Button
          variant="primary"
          id="paymentPrefSelectPaymentsContinueButton"
          onClick={handleFormSubmission}
          disabled={!hasAvailablePaymentMethods}
        >
          Continue
        </Button>
      </div>
    </>
  );
};
